
import { useLanguage } from '../../../hooks/useLanguage';
import { useMemo } from 'react';

export default function TrustBadges() {
  const { t } = useLanguage();

  // Memoize static data to avoid re‑creating arrays on every render
  const certifications = useMemo(
    () => [
      {
        icon: 'ri-verified-badge-fill',
        color: 'from-teal-500 to-emerald-500',
        bgLight: 'bg-teal-50',
        title: t('trust.100_original'),
        description: t('trust.100_original_desc'),
      },
      {
        icon: 'ri-lock-2-fill',
        color: 'from-amber-500 to-orange-500',
        bgLight: 'bg-amber-50',
        title: t('trust.secure_purchase'),
        description: t('trust.secure_purchase_desc'),
      },
      {
        icon: 'ri-timer-flash-fill',
        color: 'from-rose-500 to-pink-500',
        bgLight: 'bg-rose-50',
        title: t('trust.instant_delivery'),
        description: t('trust.instant_delivery_desc'),
      },
      {
        // Fixed typo: Tailwind color should be `to-sky-500` (was `to-sky-5` which caused a syntax error)
        icon: 'ri-shield-star-fill',
        color: 'from-cyan-500 to-sky-500',
        bgLight: 'bg-cyan-50',
        title: t('trust.lifetime_warranty'),
        description: t('trust.lifetime_warranty_desc'),
      },
      {
        icon: 'ri-customer-service-2-fill',
        color: 'from-emerald-500 to-green-500',
        bgLight: 'bg-emerald-50',
        title: t('trust.spanish_support'),
        description: t('trust.spanish_support_desc'),
      },
      {
        icon: 'ri-refund-2-fill',
        color: 'from-violet-500 to-fuchsia-500',
        bgLight: 'bg-violet-50',
        title: t('trust.money_back'),
        description: t('trust.money_back_desc'),
      },
    ],
    // Re‑run only when the translation function changes
    [t]
  );

  const paymentMethods = useMemo(
    () => [
      { name: 'MercadoPago', icon: 'ri-bank-card-fill' },
      { name: 'PayPal', icon: 'ri-paypal-fill' },
      { name: 'Visa', icon: 'ri-visa-fill' },
      { name: 'Mastercard', icon: 'ri-mastercard-fill' },
      { name: 'Transferencia', icon: 'ri-exchange-funds-fill' },
    ],
    []
  );

  const stats = useMemo(
    () => [
      { value: '+1.250', label: t('trust.satisfied_clients') },
      { value: '+1.300', label: t('trust.licenses_sold') },
      { value: '4.9/5', label: t('trust.avg_rating') },
      { value: '99.8%', label: t('trust.successful_deliveries') },
    ],
    [t]
  );

  // Defensive rendering – if translations are missing, show a fallback
  const safeRender = (content) => (content ?? '');

  return (
    <section className="py-10 sm:py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-3 sm:px-4">
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-teal-50 text-teal-700 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold uppercase tracking-wider mb-3 sm:mb-4">
            <i className="ri-award-fill"></i>
            {safeRender(t('trust.buy_confidence'))}
          </div>
          <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
            <a href="#trust" className="hover:text-teal-600 transition-colors">
              {safeRender(t('trust.why_choose'))}
            </a>
          </h4>
          <p className="text-gray-500 text-xs sm:text-sm max-w-xl mx-auto">
            {safeRender(t('trust.why_desc'))}
          </p>
        </div>

        <div id="trust" className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 mb-8 sm:mb-14">
          {certifications.map((cert, index) => (
            <div
              key={index}
              className={`${cert.bgLight} rounded-xl p-3 sm:p-6 border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group cursor-default`}
            >
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-4 text-center sm:text-left">
                <div
                  className={`w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${cert.color} text-white flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform`}
                >
                  <i className={`${cert.icon} text-base sm:text-xl`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs sm:text-sm mb-0.5 sm:mb-1">{safeRender(cert.title)}</h3>
                  <p className="text-gray-500 text-[10px] sm:text-xs leading-relaxed">{safeRender(cert.description)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#0a2540] rounded-xl sm:rounded-2xl p-5 sm:p-8 mb-8 sm:mb-14 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://public.readdy.ai/ai/img_res/da2bea68acb379ebbd6c71e9ebed4b5a.jpg"
              alt="Stats background"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl font-extrabold text-teal-400 mb-0.5 sm:mb-1">{stat.value}</div>
                <div className="text-gray-400 text-[10px] sm:text-xs font-medium uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center gap-4 sm:gap-6 border-t border-gray-100 pt-6 sm:pt-8">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
            <span className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
              {safeRender(t('trust.payment_methods'))}
            </span>
            <div className="flex items-center gap-3 sm:gap-4">
              {paymentMethods.map((method, index) => (
                <div
                  key={index}
                  className="flex items-center gap-1 sm:gap-1.5 text-gray-400 hover:text-gray-600 transition-colors cursor-default"
                  title={method.name}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${method.icon} text-base sm:text-lg`}></i>
                  </div>
                  <span className="text-[10px] sm:text-xs hidden sm:inline">{method.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <span className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
              {safeRender(t('trust.official_partners'))}
            </span>
            <div className="flex items-center gap-3 sm:gap-5 text-gray-400">
              {['Microsoft', 'Kaspersky', 'McAfee', 'ESET', 'AdGuard'].map((partner, i) => (
                <span
                  key={i}
                  className="text-[10px] sm:text-xs font-medium hover:text-gray-600 transition-colors cursor-default"
                >
                  {partner}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
